<template>
  <div>
    <b-button
      v-if="!edit"
      @click="$bvModal.show('modal-add-adobe-fonts-group-'+group.group_id)"
      class="green-button-transparent border-0"
    >
      {{ $t('buttons.addAdobeFonts') }}
    </b-button>
    <b-modal
      :id="edit ? 'modal-edit-adobe-fonts-group-' + group.group_id + '-' + tableIndex
        : 'modal-add-adobe-fonts-group-' + group.group_id"
      :ref="edit ? 'modal-edit-adobe-fonts-group-' + group.group_id + '-' + tableIndex
        : 'modal-add-adobe-fonts-group-' + group.group_id"
      size="lg"
      footer-class="flex-nowrap"
      centered
    >
      <template #modal-header>
        <h3>{{ edit ? $t('modals.editAdobeFonts.title') : $t('modals.addAdobeFonts.title') }}</h3>
      </template>

      <template #default>
        <b-row class="mt-4 mx-5 mb-4">
          <b-col cols="4">
            <label class="mb-2">{{ $t('modals.addAdobeFonts.urlLabel') }}</label>
          </b-col>
          <b-col>
            <b-form-input
              v-model="adobeFontsUrl"
              :state="urlValidationState"
              :placeholder="'https://use.typekit.net/xxxxxxx.css'"
              aria-describedby="url-feedback"
              autofocus
              debounce="500"
              @input="resetParsedFonts"
            />
            <b-form-invalid-feedback id="url-feedback">
              {{ $t('modals.addAdobeFonts.invalidUrl') }}
            </b-form-invalid-feedback>
          </b-col>
        </b-row>
        <b-row class="mx-5 mb-4">
          <b-col cols="4"></b-col>
          <b-col>
            <b-button
              variant="outline-secondary"
              size="sm"
              :disabled="!isValidUrl || isLoading"
              @click="fetchAndParseFonts"
            >
              <b-spinner v-if="isLoading" small class="mr-1"></b-spinner>
              {{ $t('modals.addAdobeFonts.detectFonts') }}
            </b-button>
          </b-col>
        </b-row>
        <b-row v-if="fetchError" class="mx-5 mb-4">
          <b-col cols="4"></b-col>
          <b-col>
            <b-alert show variant="danger" class="mb-0 py-2">
              {{ fetchError }}
            </b-alert>
          </b-col>
        </b-row>
        <b-row v-if="parsedFamilies.length > 0" class="mx-5 mb-4">
          <b-col cols="4">
            <label class="mb-2">{{ $t('modals.addAdobeFonts.detectedFonts') }}</label>
          </b-col>
          <b-col>
            <div class="detected-fonts">
              <b-badge
                v-for="family in parsedFamilies"
                :key="family"
                variant="info"
                class="mr-2 mb-2 p-2"
              >
                {{ family }}
              </b-badge>
            </div>
          </b-col>
        </b-row>
        <b-row class="mt-4 mx-5 mb-4">
          <b-col>
            <span class="text-muted">{{ $t('modals.addAdobeFonts.helpText') }}</span>
          </b-col>
        </b-row>
      </template>

      <template #modal-footer="{ close }">
        <b-button
          class="p-3 border-right-light-grey"
          block
          @click="closeModal"
        >
          {{ $t('buttons.cancel') }}
        </b-button>
        <b-button
          :disabled="!canSave"
          variant="primary"
          class="p-3 disabled-cursor"
          block
          @click="edit ? editAdobeFonts() : addAdobeFonts()"
        >
          {{ edit ? $t('modals.editAdobeFonts.validate') : $t('modals.addAdobeFonts.validate') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'ModalAddAdobeFonts',
  props: ['group', 'adobeFont', 'tableIndex', 'edit'],
  data() {
    return {
      adobeFontsUrl: this.edit ? this.adobeFont.url : '',
      parsedFamilies: this.edit ? [...this.adobeFont.families] : [],
      isLoading: false,
      fetchError: null,
    };
  },
  computed: {
    isValidUrl() {
      if (!this.adobeFontsUrl) return false;
      // Match Adobe Fonts / Typekit URLs
      const pattern = /^https:\/\/use\.typekit\.net\/[a-z0-9]+\.css$/i;
      return pattern.test(this.adobeFontsUrl.trim());
    },
    urlValidationState() {
      if (!this.adobeFontsUrl) return null;
      return this.isValidUrl;
    },
    canSave() {
      return this.isValidUrl && this.parsedFamilies.length > 0 && !this.isLoading;
    },
  },
  methods: {
    resetParsedFonts() {
      this.parsedFamilies = [];
      this.fetchError = null;
    },
    async fetchAndParseFonts() {
      if (!this.isValidUrl) return;

      this.isLoading = true;
      this.fetchError = null;
      this.parsedFamilies = [];

      try {
        const response = await fetch(this.adobeFontsUrl.trim());
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const cssText = await response.text();
        this.parsedFamilies = this.parseFontFamilies(cssText);

        if (this.parsedFamilies.length === 0) {
          this.fetchError = this.$t('modals.addAdobeFonts.noFontsFound');
        }
      } catch (error) {
        console.error('Error fetching Adobe Fonts CSS:', error);
        this.fetchError = this.$t('modals.addAdobeFonts.fetchError');
      } finally {
        this.isLoading = false;
      }
    },
    parseFontFamilies(cssText) {
      const families = new Set();

      // Match font-family declarations in @font-face rules
      const fontFaceRegex = /@font-face\s*\{[^}]*font-family:\s*["']?([^"';]+)["']?[^}]*\}/gi;
      let match = fontFaceRegex.exec(cssText);

      while (match !== null) {
        const family = match[1].trim();
        if (family) {
          families.add(family);
        }
        match = fontFaceRegex.exec(cssText);
      }

      // Also check for .tk-* class definitions which indicate font families
      const tkClassRegex = /\.tk-([a-z0-9-]+)\s*\{/gi;
      match = tkClassRegex.exec(cssText);
      while (match !== null) {
        const family = match[1].trim();
        if (family) {
          families.add(family);
        }
        match = tkClassRegex.exec(cssText);
      }

      return Array.from(families).sort();
    },
    closeModal() {
      this.resetForm();
      const modalRef = this.edit
        ? `modal-edit-adobe-fonts-group-${this.group.group_id}-${this.tableIndex}`
        : `modal-add-adobe-fonts-group-${this.group.group_id}`;
      this.$refs[modalRef].hide();
    },
    addAdobeFonts() {
      const newAdobeFont = {
        url: this.adobeFontsUrl.trim(),
        families: [...this.parsedFamilies],
      };
      this.resetForm();
      this.$emit('addAdobeFonts', newAdobeFont);
      const modalRef = `modal-add-adobe-fonts-group-${this.group.group_id}`;
      this.$refs[modalRef].hide();
    },
    editAdobeFonts() {
      const updatedAdobeFont = {
        url: this.adobeFontsUrl.trim(),
        families: [...this.parsedFamilies],
      };
      this.$emit('editAdobeFonts', updatedAdobeFont, this.tableIndex);
      const modalRef = `modal-edit-adobe-fonts-group-${this.group.group_id}-${this.tableIndex}`;
      this.$refs[modalRef].hide();
    },
    resetForm() {
      if (this.edit) {
        this.adobeFontsUrl = this.adobeFont.url;
        this.parsedFamilies = [...this.adobeFont.families];
      } else {
        this.adobeFontsUrl = '';
        this.parsedFamilies = [];
      }
      this.fetchError = null;
      this.isLoading = false;
    },
  },
};
</script>

<style scoped>
.detected-fonts {
  display: flex;
  flex-wrap: wrap;
}

.disabled-cursor:disabled {
  cursor: not-allowed;
}
</style>
